import { type ReactNode, useEffect, useMemo, useState } from "react";
import { InfoIcon, TrendingDownIcon, TrendingUpIcon } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row as TableRow_,
  useReactTable,
} from "@tanstack/react-table";

import { Column, Row, TopbarContainer, TopbarTitle } from "@/components";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useSetTitle } from "@/hooks/useSetTitle";
import { useGetJobsMetricsSummary } from "@/hooks/queries/useGetJobsMetricsSummary";
import { useGetJobsMetricsTimeseries } from "@/hooks/queries/useGetJobsMetricsTimeseries";
import { useGetStatewiseCompensationMetricsTimeseries } from "@/hooks/queries/useGetStatewiseCompensationMetricsTimeseries";
import { useGetStatewiseScrapedJobCountTimeseries } from "@/hooks/queries/useGetStatewiseScrapedJobCountTimeseries";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  JobCountsWithLocationMetricsTableRow,
  StatewiseCompensationMetricsTableRow,
} from "@/types";

// Constants / Types / Interfaces
const AREA_FILL_OPACITY = 0.5;
let metricsHeaderHoverTimer: ReturnType<typeof setTimeout> | null = null;

enum ChartColors {
  BLUE = "#2563eb",
  GREEN = "#16a34a",
  PURPLE = "#7c3aed",
}

interface DialogProps {
  state: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Configs
const chartConfig: ChartConfig = {
  totalJobs: {
    label: "Total Jobs",
    color: ChartColors.BLUE,
  },
};

const compensationChartConfig: ChartConfig = {
  jobsWithComp: { label: "With Hourly Comp", color: ChartColors.GREEN },
  jobsWithoutComp: { label: "Without Hourly Comp", color: ChartColors.PURPLE },
  compPercent: { label: "% with Hourly Comp", color: "#000000" },
};

const locationChartConfig: ChartConfig = {
  total: { label: "Total", color: ChartColors.BLUE },
  precise: { label: "Precise", color: ChartColors.GREEN },
  standard: { label: "Standard", color: ChartColors.PURPLE },
};

// Dates come in as YYYY-MM-DD (already converted to local time in the hooks).
// Splitting into integers avoids new Date() treating them as midnight UTC.
const parseDateString = (value: string): Date | null => {
  const parts = value.split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day);
};

// Formatters
const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});

const fullDateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

const formatDateLabel = (value: string) => {
  const parsed = parseDateString(value);
  if (!parsed) return value;
  return dateFormatter.format(parsed);
};

const formatFullDateLabel = (value: string) => {
  const parsed = parseDateString(value);
  if (!parsed) return value;
  return fullDateFormatter.format(parsed);
};

// the ? is so we can handle null values
const formatNumber = (value?: number | null) => {
  if (typeof value !== "number") {
    return "—";
  }

  return value.toLocaleString();
};

// Helpers
const getZIndexAndFillOpacity = (
  preciseValue: number,
  standardValue: number,
) => {
  const preciseIsSmaller = preciseValue <= standardValue;
  return {
    preciseZIndex: preciseIsSmaller ? 3 : 2,
    standardZIndex: preciseIsSmaller ? 2 : 3,
    preciseFillOpacity: preciseIsSmaller ? 1 : 0.7,
    standardFillOpacity: 0.7,
  };
};

// Components
const LocationMetricsDialog = ({ state, open, onOpenChange }: DialogProps) => {
  // We fetch on dialog open but it hits the cache due to staleTime param
  const { data: timeseries } = useGetStatewiseScrapedJobCountTimeseries(state);

  // Whichever series has a smaller value gets a higher zIndex so it renders on top and stays visible
  const latestPoint = timeseries?.[timeseries.length - 1];
  const {
    preciseZIndex,
    standardZIndex,
    preciseFillOpacity,
    standardFillOpacity,
  } = getZIndexAndFillOpacity(
    latestPoint?.precise ?? 0,
    latestPoint?.standard ?? 0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{state} Job Counts - last 30 days</DialogTitle>
        </DialogHeader>
        <div className="rounded-lg border border-slate-200 p-2">
          <ChartContainer config={locationChartConfig} className="h-[300px]">
            <AreaChart
              data={timeseries ?? []}
              margin={{ top: 20, right: 10, bottom: 0, left: 0 }}
              responsive={true}
            >
              <defs>
                <linearGradient id="fillPrecise-state" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={ChartColors.GREEN}
                    stopOpacity={AREA_FILL_OPACITY}
                  />
                  <stop
                    offset="100%"
                    stopColor={ChartColors.GREEN}
                    stopOpacity={0}
                  />
                </linearGradient>
                <linearGradient id="fillStandard-state" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={ChartColors.PURPLE}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="100%"
                    stopColor={ChartColors.PURPLE}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={formatDateLabel}
                interval="equidistantPreserveStart"
                tick={{ fontSize: 11 }}
                padding={{ left: 8, right: 8 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(v: number) => v.toLocaleString()}
                width={50}
                tick={{ fontSize: 11 }}
              />
              <ChartTooltip
                cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                content={<ChartTooltipContent />}
                labelFormatter={(value: string | number) =>
                  formatFullDateLabel(String(value))
                }
              />
              <Area
                dataKey="precise"
                name="Precise"
                type="monotoneX"
                stroke={ChartColors.GREEN}
                strokeWidth={0}
                fill="url(#fillPrecise-state)"
                fillOpacity={preciseFillOpacity}
                dot={false}
                zIndex={preciseZIndex}
              />
              <Area
                dataKey="standard"
                name="Standard"
                type="monotoneX"
                stroke={ChartColors.PURPLE}
                strokeWidth={0}
                fill="url(#fillStandard-state)"
                fillOpacity={standardFillOpacity}
                dot={false}
                zIndex={standardZIndex}
              />
              <Line
                dataKey="total"
                name="Total"
                type="monotoneX"
                stroke={ChartColors.BLUE}
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
                zIndex={4}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
              />
            </AreaChart>
          </ChartContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CompensationMetricsDialog = ({
  state,
  open,
  onOpenChange,
}: DialogProps) => {
  const { data: timeseries } =
    useGetStatewiseCompensationMetricsTimeseries(state);
  const latestPoint = timeseries?.[timeseries.length - 1];
  const {
    preciseZIndex,
    standardZIndex,
    preciseFillOpacity,
    standardFillOpacity,
  } = getZIndexAndFillOpacity(
    latestPoint?.jobsWithComp ?? 0,
    latestPoint?.jobsWithoutComp ?? 0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{state} Comp Coverage - last 30 days</DialogTitle>
        </DialogHeader>
        <div className="rounded-lg border border-slate-200 p-2">
          <ChartContainer
            config={compensationChartConfig}
            className="h-[300px]"
          >
            <AreaChart
              data={timeseries ?? []}
              margin={{ top: 20, right: 10, bottom: 0, left: 0 }}
              responsive={true}
            >
              <defs>
                <linearGradient id="fillWithComp" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={ChartColors.GREEN}
                    stopOpacity={0.5}
                  />
                  <stop
                    offset="100%"
                    stopColor={ChartColors.GREEN}
                    stopOpacity={0}
                  />
                </linearGradient>
                <linearGradient id="fillWithoutComp" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={ChartColors.PURPLE}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="100%"
                    stopColor={ChartColors.PURPLE}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={formatDateLabel}
                interval="equidistantPreserveStart"
                tick={{ fontSize: 11 }}
                padding={{ left: 8, right: 8 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(v: number) => v.toLocaleString()}
                width={50}
                tick={{ fontSize: 11 }}
              />
              <ChartTooltip
                cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                content={<ChartTooltipContent />}
                labelFormatter={(value: string | number) =>
                  formatFullDateLabel(String(value))
                }
              />
              <Area
                dataKey="jobsWithComp"
                name="With Comp"
                type="monotoneX"
                stroke={ChartColors.GREEN}
                strokeWidth={0}
                fill="url(#fillWithComp)"
                fillOpacity={preciseFillOpacity}
                dot={false}
                zIndex={preciseZIndex}
              />
              <Area
                dataKey="jobsWithoutComp"
                name="Without Comp"
                type="monotoneX"
                stroke={ChartColors.PURPLE}
                strokeWidth={0}
                fill="url(#fillWithoutComp)"
                fillOpacity={standardFillOpacity}
                dot={false}
                zIndex={standardZIndex}
              />
              <Line
                dataKey="compPercent"
                name="% with Comp"
                type="monotoneX"
                stroke="#000000"
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
                zIndex={5}
                yAxisId="percent"
              />
              <YAxis
                yAxisId="percent"
                orientation="right"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(v: number) => `${String(v)}%`}
                width={45}
                tick={{ fontSize: 11 }}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
              />
            </AreaChart>
          </ChartContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StateCell = ({ state }: { state: string }) => {
  const { data: timeseries } = useGetStatewiseScrapedJobCountTimeseries(state);

  const trend = useMemo<"up" | "down" | null>(() => {
    if (!timeseries || timeseries.length < 2) return null;
    const last = timeseries[timeseries.length - 1]?.total ?? 0;
    const prev = timeseries[timeseries.length - 2]?.total ?? 0;
    return last >= prev ? "up" : "down";
  }, [timeseries]);

  return (
    <span className="inline-flex items-center gap-1.5 font-medium">
      {state}
      {trend === "up" && (
        <TrendingUpIcon className="h-3.5 w-3.5 text-emerald-500" />
      )}
      {trend === "down" && (
        <TrendingDownIcon className="h-3.5 w-3.5 text-rose-500" />
      )}
    </span>
  );
};

export const MetricsPage = () => {
  useSetTitle({
    title: "Metrics",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.METRICS);
  }, []);

  const {
    data: summary,
    status: summaryStatus,
    error: summaryError,
  } = useGetJobsMetricsSummary();

  const {
    data: timeseries,
    status: timeseriesStatus,
    error: timeseriesError,
  } = useGetJobsMetricsTimeseries();

  const latestPoint = useMemo(() => {
    if (!timeseries?.length) {
      return undefined;
    }
    return timeseries[timeseries.length - 1];
  }, [timeseries]);

  const previousPoint = useMemo(() => {
    if (!timeseries || timeseries.length < 2) {
      return undefined;
    }
    return timeseries[timeseries.length - 2];
  }, [timeseries]);

  const totalJobsDelta = useMemo(() => {
    if (!latestPoint || !previousPoint) {
      return undefined;
    }
    // backend may return null values for any of the metrics so we need to handle that
    if (
      typeof latestPoint.totalJobs !== "number" ||
      typeof previousPoint.totalJobs !== "number"
    ) {
      return undefined;
    }

    const delta = latestPoint.totalJobs - previousPoint.totalJobs;
    const percent =
      previousPoint.totalJobs === 0
        ? undefined
        : (delta / previousPoint.totalJobs) * 100;

    return {
      delta,
      percent,
      isPositive: delta >= 0,
    };
  }, [latestPoint, previousPoint]);

  const chartData = useMemo(() => {
    if (!timeseries?.length) {
      return [];
    }

    return timeseries.map((entry) => ({
      ...entry,
      dateLabel: formatDateLabel(entry.date),
    }));
  }, [timeseries]);

  // TODO: `state` is very common word, we need to treat it as a reserved word and find a better prefix.
  const [activeTab, setActiveTab] = useState<"location" | "compensation">(
    "location",
  );
  const [selectedCompensationState, setSelectedCompensationState] = useState<
    string | null
  >(null);
  const [selectedLocationState, setSelectedLocationState] = useState<
    string | null
  >(null);

  const {
    data: jobLocationData,
    isPending: isJobLocationPending,
    isError: isJobLocationError,
  } = useGetStatewiseScrapedJobCountTimeseries(null);

  const jobLocationColumns: ColumnDef<JobCountsWithLocationMetricsTableRow>[] =
    useMemo(
      () => [
        {
          accessorKey: "state",
          header: "State",
          cell: ({ row }) => <StateCell state={row.original.state} />,
        },
        {
          accessorKey: "preciseLocationJobCount",
          header: "Jobs with precise locations",
          cell: ({ row }) =>
            formatNumber(Number(row.original.preciseLocationJobCount)),
        },
        {
          accessorKey: "standardLocationJobCount",
          header: "Jobs with standard locations",
          cell: ({ row }) =>
            formatNumber(Number(row.original.standardLocationJobCount)),
        },
        {
          accessorKey: "total",
          header: "Total",
          cell: ({ row }) => formatNumber(Number(row.original.total)),
        },
        {
          id: "precisePercent",
          header: "% precise",
          accessorFn: (row) =>
            row.total > 0
              ? Math.round((row.preciseLocationJobCount / row.total) * 1000) /
                10
              : 0,
          cell: ({ getValue }) => `${Number(getValue()).toFixed(1)}%`,
        },
      ],
      [],
    );

  const locationTable = useReactTable({
    data: jobLocationData ?? [],
    columns: jobLocationColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: { sorting: [{ id: "total", desc: true }] },
  });

  const compensationColumns: ColumnDef<StatewiseCompensationMetricsTableRow>[] =
    useMemo(
      () => [
        {
          accessorKey: "state",
          header: "State",
          cell: ({ getValue }) => <StateCell state={String(getValue())} />,
        },
        {
          accessorKey: "jobsWithCompensation",
          header: "Jobs with hourly compensation",
          cell: ({ getValue }) => formatNumber(Number(getValue())),
        },
        {
          accessorKey: "totalJobs",
          header: () => (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center gap-1"
                    onMouseEnter={() => {
                      metricsHeaderHoverTimer = setTimeout(() => {
                        mixpanelUtils.trackHover(
                          "Total header info icon",
                          MIXPANEL_SCREENS.METRICS,
                        );
                      }, 300);
                    }}
                    onMouseLeave={() => {
                      if (metricsHeaderHoverTimer) {
                        clearTimeout(metricsHeaderHoverTimer);
                        metricsHeaderHoverTimer = null;
                      }
                    }}
                  >
                    Total
                    <InfoIcon className="h-3.5 w-3.5 text-slate-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[220px] text-center">
                  Job counts here are sourced from Cloud Talent and may be lower
                  than the database. This is a known inconsistency being
                  actively worked on.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ),
          cell: ({ getValue }) => formatNumber(Number(getValue())),
        },
        {
          accessorKey: "compensationPercent",
          header: "% with hourly compensation",
          cell: ({ getValue }) => `${Number(getValue()).toFixed(1)}%`,
        },
      ],
      [],
    );

  const { data: compensationSnapshotData } =
    useGetStatewiseCompensationMetricsTimeseries(null);

  const compensationTable = useReactTable({
    data: compensationSnapshotData ?? [],
    columns: compensationColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: { sorting: [{ id: "totalJobs", desc: true }] },
  });

  const locationStats = useMemo(() => {
    const data = jobLocationData ?? [];
    const totalPrecise = data.reduce(
      (sum, r) => sum + r.preciseLocationJobCount,
      0,
    );
    const totalJobs = data.reduce((sum, r) => sum + r.total, 0);
    return {
      totalPrecise,
      precisePercent: totalJobs > 0 ? (totalPrecise / totalJobs) * 100 : 0,
    };
  }, [jobLocationData]);

  const compensationStats = useMemo(() => {
    const data = compensationSnapshotData ?? [];
    const totalWithComp = data.reduce(
      (sum, r) => sum + r.jobsWithCompensation,
      0,
    );
    const totalJobs = data.reduce((sum, r) => sum + r.totalJobs, 0);
    return {
      totalWithComp,
      compPercent: totalJobs > 0 ? (totalWithComp / totalJobs) * 100 : 0,
    };
  }, [compensationSnapshotData]);

  const isLoadingTimeseries = timeseriesStatus === "pending";

  const hasTimeseriesError = timeseriesStatus === "error";

  let summaryContent: ReactNode;
  switch (summaryStatus) {
    case "pending":
      summaryContent = <Skeleton className="h-20 rounded-lg" />;
      break;
    case "error":
      summaryContent = (
        <p className="text-sm text-slate-500">
          Unable to load latest counts.{" "}
          {summaryError instanceof Error ? summaryError.message : null}
        </p>
      );
      break;
    case "success":
      summaryContent = (
        <Column className="gap-2 rounded-lg border border-slate-100 bg-slate-50 p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">
            Total jobs
          </p>
          <p className="text-2xl font-semibold text-slate-900">
            {formatNumber(summary.totalJobs)}
          </p>
        </Column>
      );
      break;
    default:
      summaryContent = (
        <p className="text-sm text-slate-500">No summary data available yet.</p>
      );
  }

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle>Metrics</TopbarTitle>
      </TopbarContainer>
      <Column className="p-6 pb-3">
        <Card>
          <CardHeader>
            <CardTitle>Current job counts</CardTitle>
            <CardDescription>Latest total job count.</CardDescription>
          </CardHeader>
          <CardContent>{summaryContent}</CardContent>
          <CardFooter className="flex-col items-start gap-2 text-sm text-slate-600">
            {totalJobsDelta ? (
              <>
                <Row className="items-center gap-2 font-medium text-slate-900">
                  {totalJobsDelta.isPositive ? (
                    <TrendingUpIcon className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <TrendingDownIcon className="h-4 w-4 text-rose-500" />
                  )}
                  {totalJobsDelta.isPositive ? "Trending up" : "Trending down"}{" "}
                  by {Math.abs(totalJobsDelta.delta).toLocaleString()} jobs
                  {typeof totalJobsDelta.percent === "number"
                    ? ` (${Math.abs(totalJobsDelta.percent).toFixed(1)}%)`
                    : ""}
                  {" compared to the previous day."}
                </Row>
                {latestPoint ? (
                  <p className="text-xs text-slate-500">
                    Latest update: {formatFullDateLabel(latestPoint.date)}
                  </p>
                ) : null}
              </>
            ) : latestPoint ? (
              <p className="text-xs text-slate-500">
                Latest update: {formatFullDateLabel(latestPoint.date)}
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                Waiting for the first datapoint.
              </p>
            )}
          </CardFooter>
        </Card>
      </Column>
      <Column className="p-6 pb-3">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle>Current job counts by state</CardTitle>
            <CardDescription>
              {activeTab === "location"
                ? "Precise and standard location job counts per state. Sourced from the database."
                : "Jobs with hourly compensation data per state. Sourced directly from Cloud Talent."}
              {" Click a row to view that state\u2019s trend over time."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs
              defaultValue="location"
              onValueChange={(v) => {
                setActiveTab(v as "location" | "compensation");
                mixpanelUtils.trackTabView(MIXPANEL_SCREENS.METRICS, v);
              }}
            >
              <Row className="mb-4 items-center justify-between">
                <TabsList>
                  <TabsTrigger value="location">Location</TabsTrigger>
                  <TabsTrigger value="compensation">Compensation</TabsTrigger>
                </TabsList>
                {activeTab === "location" ? (
                  <Row className="gap-3">
                    <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 shadow-sm">
                      <p className="text-xs text-slate-500">
                        Total Precise Locations
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {formatNumber(locationStats.totalPrecise)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 shadow-sm">
                      <p className="text-xs text-slate-500">Precise Coverage</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {locationStats.precisePercent.toFixed(1)}%
                      </p>
                    </div>
                  </Row>
                ) : (
                  <Row className="gap-3">
                    <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 shadow-sm">
                      <p className="text-xs text-slate-500">Total with Comp</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {formatNumber(compensationStats.totalWithComp)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 shadow-sm">
                      <p className="text-xs text-slate-500">Comp Coverage</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {compensationStats.compPercent.toFixed(1)}%
                      </p>
                    </div>
                  </Row>
                )}
              </Row>
              <TabsContent value="location">
                {isJobLocationPending ? (
                  <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                    <Skeleton className="h-40 rounded-lg" />
                  </div>
                ) : isJobLocationError ? (
                  <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <p className="p-6 text-sm text-slate-500">
                      Unable to load location metrics.
                    </p>
                  </div>
                ) : (
                  <Column>
                    <div className="rounded-t-lg border border-b-0 border-slate-200">
                      <Table className="table-fixed">
                        <colgroup>
                          <col className="w-[10%]" />
                          <col className="w-[22%]" />
                          <col className="w-[22%]" />
                          <col className="w-[22%]" />
                          <col className="w-[24%]" />
                        </colgroup>
                        <TableHeader>
                          {locationTable
                            .getHeaderGroups()
                            .map((headerGroup) => (
                              <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                  <TableHead
                                    key={header.id}
                                    className="text-center"
                                  >
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext(),
                                    )}
                                  </TableHead>
                                ))}
                              </TableRow>
                            ))}
                        </TableHeader>
                      </Table>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto rounded-b-lg border border-slate-200">
                      <Table className="table-fixed">
                        <colgroup>
                          <col className="w-[10%]" />
                          <col className="w-[22%]" />
                          <col className="w-[22%]" />
                          <col className="w-[22%]" />
                          <col className="w-[24%]" />
                        </colgroup>
                        <TableBody>
                          {locationTable.getRowModel().rows.map((row) => (
                            <TableRow
                              key={row.id}
                              className="cursor-pointer hover:bg-slate-50"
                              onClick={() => {
                                mixpanelUtils.trackRowClick("Location metrics state", MIXPANEL_SCREENS.METRICS);
                                setSelectedLocationState(row.original.state);
                              }}
                            >
                              {row.getVisibleCells().map((cell) => (
                                <TableCell
                                  key={cell.id}
                                  className="text-center"
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </Column>
                )}
              </TabsContent>
              <TabsContent value="compensation">
                <Column>
                  <div className="rounded-t-lg border border-b-0 border-slate-200">
                    <Table className="table-fixed">
                      <colgroup>
                        <col className="w-[10%]" />
                        <col className="w-[25%]" />
                        <col className="w-[25%]" />
                        <col className="w-[40%]" />
                      </colgroup>
                      <TableHeader>
                        {compensationTable
                          .getHeaderGroups()
                          .map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <TableHead
                                  key={header.id}
                                  className="text-center"
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                </TableHead>
                              ))}
                            </TableRow>
                          ))}
                      </TableHeader>
                    </Table>
                  </div>
                  <div className="max-h-[500px] overflow-y-auto rounded-b-lg border border-slate-200">
                    <Table className="table-fixed">
                      <colgroup>
                        <col className="w-[10%]" />
                        <col className="w-[25%]" />
                        <col className="w-[25%]" />
                        <col className="w-[40%]" />
                      </colgroup>
                      <TableBody>
                        {compensationTable
                          .getRowModel()
                          .rows.map(
                            (
                              row: TableRow_<StatewiseCompensationMetricsTableRow>,
                            ) => (
                              <TableRow
                                key={row.id}
                                className="cursor-pointer hover:bg-slate-50"
                                onClick={() => {
                                  mixpanelUtils.trackRowClick("Compensation metrics state", MIXPANEL_SCREENS.METRICS);
                                  setSelectedCompensationState(
                                    String(row.getAllCells()[0]?.getValue()),
                                  );
                                }}
                              >
                                {row.getVisibleCells().map((cell) => (
                                  <TableCell
                                    key={cell.id}
                                    className="text-center"
                                  >
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ),
                          )}
                      </TableBody>
                    </Table>
                  </div>
                </Column>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </Column>
      {selectedLocationState && (
        <LocationMetricsDialog
          state={selectedLocationState}
          open={true}
          onOpenChange={(open: boolean) => {
            if (!open) setSelectedLocationState(null);
          }}
        />
      )}
      {selectedCompensationState && (
        <CompensationMetricsDialog
          state={selectedCompensationState}
          open={true}
          onOpenChange={(open) => {
            if (!open) setSelectedCompensationState(null);
          }}
        />
      )}
      <Row className="p-6 pb-3">
        <Card className="flex-1">
          <CardHeader>
            <CardTitle>Job totals over time</CardTitle>
            <CardDescription>
              Historical total job count over time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingTimeseries ? (
              <Skeleton className="h-[320px] w-full rounded-lg" />
            ) : hasTimeseriesError ? (
              <p className="text-sm text-slate-500">
                Unable to load historical metrics.{" "}
                {timeseriesError instanceof Error
                  ? timeseriesError.message
                  : null}
              </p>
            ) : chartData.length ? (
              <div className="rounded-lg border border-slate-200 p-4">
                <ChartContainer config={chartConfig} className="h-[360px]">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient
                        id="fillTotalJobs"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={ChartColors.BLUE}
                          stopOpacity={AREA_FILL_OPACITY}
                        />
                        <stop
                          offset="100%"
                          stopColor="#ffffff"
                          stopOpacity={1}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={formatDateLabel}
                      interval="equidistantPreserveStart"
                      tick={{ fontSize: 11 }}
                      padding={{ left: 16, right: 16 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={(v: number) => v.toLocaleString()}
                      width={65}
                      tick={{ fontSize: 11 }}
                    />
                    <ChartTooltip
                      cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                      content={<ChartTooltipContent />}
                      labelFormatter={(value: string | number) =>
                        formatFullDateLabel(String(value))
                      }
                    />
                    <Area
                      dataKey="totalJobs"
                      name={chartConfig.totalJobs.label}
                      type="monotoneX"
                      stroke={ChartColors.BLUE}
                      strokeWidth={0}
                      fill="url(#fillTotalJobs)"
                      dot={false}
                    />
                  </AreaChart>
                </ChartContainer>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                No historical metrics available yet.
              </p>
            )}
          </CardContent>
        </Card>
      </Row>
    </Column>
  );
};
